/**
 * DESIGN SYSTEM NOTE: Hard-coded Tailwind colors in this file (bg-[#ECF1F3], text-[#43798F], etc.)
 * are intentional and match approved Figma designs. Platform CSS variables will be updated
 * to match Figma in a future initiative, then this file will be refactored to use semantic tokens.
 * See PR #9637 for context on the Figma-first strategy.
 */

import { observer } from 'mobx-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useContext, useState } from 'react';
import { T, TranslationsContext, translation } from '../../../../../../../../Translations';
import FilterSection from './FilterSection';
import FilterStore from '../../../../Builder/ExperienceCatalog/FilterTemplate/Store';
import UnlicensedToggleStore from '../../../Filters/UnlicensedToggleStore';
import { FilterOptionData } from '../../../../Builder/ExperienceCatalog/Types';
import { ShowUnlicensedToggle } from '../../../Filters/FilterOptionsTypes';
import { when } from '@execonline-inc/maybe-adapter';
import { equals } from '@execonline-inc/collections';
import { trackIntercomEvent } from '../../../../../../../../services/intercomTrackingService';

interface Props {
  onClose: () => void;
  productTypeFilterStore: FilterStore;
  schoolPartnerFilterStore: FilterStore;
  languageFilterStore: FilterStore;
  durationFilterStore: FilterStore;
  licenseFilterStore: FilterStore;
  competencyFilterStore: FilterStore;
  productTypeOptions: FilterOptionData[];
  schoolPartnerOptions: FilterOptionData[];
  languageOptions: FilterOptionData[];
  durationOptions: FilterOptionData[];
  licenseOptions: FilterOptionData[];
  competencyOptions: FilterOptionData[];
  unlicensedToggleStore: UnlicensedToggleStore;
  showUnlicensedToggle: ShowUnlicensedToggle;
  activeFiltersCount: number;
}

function FilterDrawer({
  onClose,
  productTypeFilterStore,
  schoolPartnerFilterStore,
  languageFilterStore,
  durationFilterStore,
  licenseFilterStore,
  competencyFilterStore,
  productTypeOptions,
  schoolPartnerOptions,
  languageOptions,
  durationOptions,
  licenseOptions,
  competencyOptions,
  unlicensedToggleStore,
  showUnlicensedToggle,
  activeFiltersCount,
}: Props) {
  const ts = useContext(TranslationsContext);
  // Start with all sections collapsed
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section],
    );
  };

  const handleClearAllFilters = () => {
    trackIntercomEvent('Builder V2 - Course Catalog - Clear All Filters');

    // Clear all filters by opening and selecting "All" option
    productTypeFilterStore.open();
    schoolPartnerFilterStore.open();
    languageFilterStore.open();
    durationFilterStore.open();
    licenseFilterStore.open();
    competencyFilterStore.open();

    // Reset to initial state (all options selected = no filter)
    productTypeFilterStore.filterAllValues({
      kind: 'all-options',
      label: 'All',
      value: 'All',
      count: productTypeOptions.length,
    });
    schoolPartnerFilterStore.filterAllValues({
      kind: 'all-options',
      label: 'All',
      value: 'All',
      count: schoolPartnerOptions.length,
    });
    languageFilterStore.filterAllValues({
      kind: 'all-options',
      label: 'All',
      value: 'All',
      count: languageOptions.length,
    });
    durationFilterStore.filterAllValues({
      kind: 'all-hours',
      label: 'All',
      value: 'All',
      count: durationOptions.length,
    });
    licenseFilterStore.filterAllValues({
      kind: 'all-options',
      label: 'All',
      value: 'All',
      count: licenseOptions.length,
    });
    competencyFilterStore.filterAllValues({
      kind: 'all-options',
      label: 'All',
      value: 'All',
      count: competencyOptions.length,
    });
    unlicensedToggleStore.toggle();
    if (unlicensedToggleStore.showUnlicensed) {
      unlicensedToggleStore.toggle();
    }
  };

  const isFilterActive = (store: FilterStore, value: string) => {
    return store.state.filter.options.includes(value);
  };

  const toggleFilter = (store: FilterStore, option: FilterOptionData, filterType: string) => {
    const isActive = isFilterActive(store, option.value);
    trackIntercomEvent('Builder V2 - Course Catalog - Toggle Filter', {
      filterType,
      filterLabel: option.label,
      filterValue: option.value,
      action: isActive ? 'remove' : 'add',
    });

    store.open();
    store.filterAllValues({
      kind: 'some-options',
      label: option.label,
      value: option.value,
      count: option.count,
    });
  };

  const toggleDurationFilter = (store: FilterStore, option: FilterOptionData) => {
    const isActive = isFilterActive(store, option.value);
    trackIntercomEvent('Builder V2 - Course Catalog - Toggle Filter', {
      filterType: 'Duration',
      filterLabel: option.label,
      filterValue: option.value,
      action: isActive ? 'remove' : 'add',
    });

    store.open();
    const hasDuration = Number(option.value) > 0;
    store.filterAllValues(
      hasDuration
        ? {
            kind: 'some-hours',
            label: option.label,
            value: option.value,
            count: option.count,
          }
        : {
            kind: 'no-hour',
            label: 'None',
            value: option.value,
            count: option.count,
          },
    );
  };

  const handleUnlicensedToggle = () => {
    trackIntercomEvent('Builder V2 - Course Catalog - Toggle Unlicensed', {
      action: unlicensedToggleStore.showUnlicensed ? 'hide' : 'show',
    });
    unlicensedToggleStore.toggle();
  };

  const handleClose = () => {
    trackIntercomEvent('Builder V2 - Course Catalog - Close Filters');
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 z-10"
        onClick={handleClose}
        data-intercom-target="builder-v2-filter-drawer-backdrop"
      />

      {/* Drawer */}
      <div
        className="absolute top-0 left-0 h-full w-2/5 bg-card shadow-xl flex flex-col z-20"
        data-intercom-target="builder-v2-filter-drawer"
      >
        {/* Header */}
        <div className="border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between p-6 pb-3">
            <div className="flex items-center gap-3">
              <h3>
                <T kind="Filters" />
              </h3>
              {activeFiltersCount > 0 && (
                <span
                  className="px-2 py-0.5 bg-muted text-foreground rounded-full"
                  style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              aria-label={translation('Close filters')(ts)}
              data-intercom-target="builder-v2-close-filter-drawer"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          {activeFiltersCount > 0 && (
            <div className="px-6 pb-3">
              <button
                onClick={handleClearAllFilters}
                className="text-muted-foreground hover:text-foreground transition-colors"
                style={{ fontSize: 'var(--text-sm)' }}
                data-intercom-target="builder-v2-clear-all-filters"
              >
                <T kind="Clear filters" />
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Licensed Toggle */}
          {when(equals(showUnlicensedToggle, 'show'), {})
            .map(() => (
              <div className="pb-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <span
                    style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                  >
                    <T kind="Show unlicensed courses" />
                  </span>
                  <button
                    onClick={handleUnlicensedToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors hover:opacity-80 ${
                      unlicensedToggleStore.showUnlicensed ? 'bg-primary' : 'bg-muted'
                    }`}
                    data-intercom-target="builder-v2-unlicensed-toggle"
                    aria-label={translation('Toggle unlicensed courses')(ts)}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        unlicensedToggleStore.showUnlicensed ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            ))
            .getOrElse(() => (
              <></>
            ))}

          <FilterSection
            sectionKey="license"
            titleKey="License"
            filterStore={licenseFilterStore}
            options={licenseOptions}
            isExpanded={expandedSections.includes('license')}
            onToggleSection={toggleSection}
            onOptionClick={(option) => toggleFilter(licenseFilterStore, option, 'License')}
            isOptionActive={(value) => isFilterActive(licenseFilterStore, value)}
            intercomTarget="builder-v2-license-filter-pill"
          />

          <FilterSection
            sectionKey="courseType"
            titleKey="Course Type"
            filterStore={productTypeFilterStore}
            options={productTypeOptions}
            isExpanded={expandedSections.includes('courseType')}
            onToggleSection={toggleSection}
            onOptionClick={(option) => toggleFilter(productTypeFilterStore, option, 'Course Type')}
            isOptionActive={(value) => isFilterActive(productTypeFilterStore, value)}
            intercomTarget="builder-v2-course-type-filter-pill"
          />

          <FilterSection
            sectionKey="school"
            titleKey="School Partner"
            filterStore={schoolPartnerFilterStore}
            options={schoolPartnerOptions}
            isExpanded={expandedSections.includes('school')}
            onToggleSection={toggleSection}
            onOptionClick={(option) =>
              toggleFilter(schoolPartnerFilterStore, option, 'School Partner')
            }
            isOptionActive={(value) => isFilterActive(schoolPartnerFilterStore, value)}
            intercomTarget="builder-v2-school-filter-pill"
          />

          <FilterSection
            sectionKey="language"
            titleKey="Language"
            filterStore={languageFilterStore}
            options={languageOptions}
            isExpanded={expandedSections.includes('language')}
            onToggleSection={toggleSection}
            onOptionClick={(option) => toggleFilter(languageFilterStore, option, 'Language')}
            isOptionActive={(value) => isFilterActive(languageFilterStore, value)}
            intercomTarget="builder-v2-language-filter-pill"
          />

          <FilterSection
            sectionKey="duration"
            titleKey="Time Commitment"
            filterStore={durationFilterStore}
            options={durationOptions}
            isExpanded={expandedSections.includes('duration')}
            onToggleSection={toggleSection}
            onOptionClick={(option) => toggleDurationFilter(durationFilterStore, option)}
            isOptionActive={(value) => isFilterActive(durationFilterStore, value)}
            intercomTarget="builder-v2-duration-filter-pill"
          />

          <FilterSection
            sectionKey="competency"
            titleKey="Competencies"
            filterStore={competencyFilterStore}
            options={competencyOptions}
            isExpanded={expandedSections.includes('competency')}
            onToggleSection={toggleSection}
            onOptionClick={(option) => toggleFilter(competencyFilterStore, option, 'Competency')}
            isOptionActive={(value) => isFilterActive(competencyFilterStore, value)}
            intercomTarget="builder-v2-competency-filter-pill"
          />
        </div>
      </div>
    </>
  );
}

export default observer(FilterDrawer);
